/**
 * Offline copies of the files that matter in a crisis: everything in
 * the Vault (vital + retired) plus starred key documents.
 *
 * The metadata layer is already offline-first (TanStack Query persists
 * to IndexedDB); this closes the gap for the bytes. When enabled (a
 * per-device choice), every file in the offline set is downloaded into
 * a named Cache Storage cache and kept current by a hash-diffed sync
 * that runs whenever the app is open online: new records download,
 * replaced scans re-download (fileHash changed), removed/unstarred
 * ones are pruned. The FileViewer falls back to these copies when the
 * network is gone.
 *
 * Sync fetches carry a `?pin=1` marker so the service worker's
 * recently-viewed runtime cache ignores them (vite.config.ts) — the
 * bytes land only here, stored under the canonical file URL so a
 * single `caches.match` finds either copy.
 *
 * This is an availability layer, not a backup: R2 is canonical and
 * Dropbox is the human-facing mirror. The browser can, in principle,
 * evict Cache Storage under extreme disk pressure — enabling asks for
 * persistent storage to make that as unlikely as the platform allows.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { DocumentDTO } from '@almanac/shared';
import { api } from './api.js';
import { isRetiredRecord, isVaultRecord } from './documents.js';

const CACHE_NAME = 'offline-vault';
const ENABLED_KEY = 'almanac-offline-vault';
const MANIFEST_KEY = 'almanac-offline-vault-manifest';
const CHECKED_KEY = 'almanac-offline-vault-checked';

/** docId → the fileHash whose bytes are in the cache. */
type Manifest = Record<string, string>;

/** The documents whose files are kept on this device: the Vault,
 *  retired records, starred key documents — and anything linked to a
 *  trip that hasn't ended yet (tickets and bookings must work in a
 *  dead spot; `keptTripIds` is the caller's set of active trips). */
export function offlineFileSet(
  docs: DocumentDTO[],
  keptTripIds?: ReadonlySet<string>,
): DocumentDTO[] {
  return docs.filter(
    (d) =>
      Boolean(d.fileName) &&
      (isVaultRecord(d) ||
        isRetiredRecord(d) ||
        d.starred ||
        Boolean(d.tripId && keptTripIds?.has(d.tripId))),
  );
}

/** The change signal for one document's bytes. Files uploaded before
 *  hashing existed fall back to updatedAt — worst case an extra
 *  re-download, never a stale copy. */
function versionOf(doc: DocumentDTO): string {
  return doc.fileHash ?? `updated:${doc.updatedAt}`;
}

export function offlineEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === '1';
  } catch {
    return false;
  }
}

function readManifest(): Manifest {
  try {
    const raw = localStorage.getItem(MANIFEST_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Manifest) : {};
  } catch {
    return {};
  }
}

function writeManifest(manifest: Manifest) {
  try {
    localStorage.setItem(MANIFEST_KEY, JSON.stringify(manifest));
  } catch {
    // Best-effort — a failed write just means a re-download later.
  }
}

export function lastCheckedAt(): string | null {
  try {
    return localStorage.getItem(CHECKED_KEY);
  } catch {
    return null;
  }
}

/**
 * What a sync run would do — pure, so the selection and diff rules are
 * unit-testable without Cache Storage.
 */
export function offlineSyncPlan(
  docs: DocumentDTO[],
  manifest: Manifest,
  keptTripIds?: ReadonlySet<string>,
): { fetch: DocumentDTO[]; drop: string[] } {
  const set = offlineFileSet(docs, keptTripIds);
  const wanted = new Map(set.map((d) => [d.id, d]));
  return {
    fetch: set.filter((d) => manifest[d.id] !== versionOf(d)),
    drop: Object.keys(manifest).filter((id) => !wanted.has(id)),
  };
}

/** What's on this device right now, per the manifest. */
export function offlineSummary(docs: DocumentDTO[]): { files: number; bytes: number } {
  const manifest = readManifest();
  const byId = new Map(docs.map((d) => [d.id, d]));
  let files = 0;
  let bytes = 0;
  for (const id of Object.keys(manifest)) {
    const doc = byId.get(id);
    if (!doc) continue;
    files += 1;
    bytes += doc.fileSize ?? 0;
  }
  return { files, bytes };
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${Math.max(1, Math.round(bytes / (1024 * 1024)))} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

// One sync at a time across the whole app — the Layout-level syncer
// and the Vault card share this lock instead of racing.
let syncing = false;

/**
 * Bring the on-device copies up to date. Fetch failures are skipped
 * (retried on the next pass) — a flaky connection must never wedge the
 * whole set.
 */
export async function syncOfflineFiles(
  docs: DocumentDTO[],
  onProgress?: (done: number, total: number) => void,
  keptTripIds?: ReadonlySet<string>,
): Promise<void> {
  if (syncing || typeof caches === 'undefined') return;
  syncing = true;
  try {
    const manifest = readManifest();
    const plan = offlineSyncPlan(docs, manifest, keptTripIds);
    const cache = await caches.open(CACHE_NAME);

    for (const id of plan.drop) {
      await cache.delete(api.documentFileUrl(id)).catch(() => {});
      delete manifest[id];
    }

    let done = 0;
    for (const doc of plan.fetch) {
      onProgress?.(done + 1, plan.fetch.length);
      try {
        const res = await fetch(`${api.documentFileUrl(doc.id)}?pin=1`, {
          credentials: 'include',
          redirect: 'manual',
        });
        if (res.type === 'opaqueredirect' || !res.ok) continue;
        await cache.put(api.documentFileUrl(doc.id), res);
        manifest[doc.id] = versionOf(doc);
      } catch {
        // Offline mid-sync or a flaky request — next pass picks it up.
      }
      done += 1;
    }

    writeManifest(manifest);
    try {
      localStorage.setItem(CHECKED_KEY, new Date().toISOString());
    } catch {
      // Best-effort.
    }
  } finally {
    syncing = false;
  }
}

export async function clearOfflineFiles(): Promise<void> {
  try {
    localStorage.removeItem(MANIFEST_KEY);
    localStorage.removeItem(CHECKED_KEY);
  } catch {
    // Best-effort.
  }
  if (typeof caches !== 'undefined') await caches.delete(CACHE_NAME).catch(() => {});
}

/** The on-device copy of a file, if any cache holds one (the pinned
 *  set or the recently-viewed runtime cache). */
export async function cachedFileResponse(documentId: string): Promise<Response | undefined> {
  if (typeof caches === 'undefined') return undefined;
  try {
    return await caches.match(api.documentFileUrl(documentId));
  } catch {
    return undefined;
  }
}

/**
 * The per-device switch + sync state, shared by the Vault card and the
 * silent app-level syncer. Enabling asks the browser for persistent
 * storage (hardens against eviction) and pre-warms the lazily-loaded
 * PDF renderer so cached PDFs actually render offline.
 */
export function useOfflineVault(
  docs: DocumentDTO[] | undefined,
  keptTripIds?: ReadonlySet<string>,
) {
  const [enabled, setEnabled] = useState(offlineEnabled);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // Bumped after every sync so summaries recompute from the manifest.
  const [, setSyncStamp] = useState(0);
  const docsRef = useRef(docs);
  docsRef.current = docs;
  const tripsRef = useRef(keptTripIds);
  tripsRef.current = keptTripIds;

  const runSync = useCallback(async () => {
    const current = docsRef.current;
    if (!current || !navigator.onLine) return;
    await syncOfflineFiles(current, (done, total) => setProgress({ done, total }), tripsRef.current);
    setProgress(null);
    setSyncStamp((n) => n + 1);
  }, []);

  // Keep the set current whenever the app is open online: on load,
  // when the document list changes, and when the connection returns.
  useEffect(() => {
    if (!enabled || !docs) return;
    void runSync();
    window.addEventListener('online', runSync);
    return () => window.removeEventListener('online', runSync);
  }, [enabled, docs, runSync]);

  const enable = useCallback(() => {
    try {
      localStorage.setItem(ENABLED_KEY, '1');
    } catch {
      // Ignore — the toggle just won't survive a reload.
    }
    setEnabled(true);
    // Harden the cache against eviction where the platform allows.
    void navigator.storage?.persist?.().catch(() => {});
    // Warm the pdf.js chunk into its runtime cache for offline PDFs.
    void import('../components/PdfView.js').catch(() => {});
    void runSync();
  }, [runSync]);

  const disable = useCallback(() => {
    try {
      localStorage.removeItem(ENABLED_KEY);
    } catch {
      // Ignore.
    }
    setEnabled(false);
    setProgress(null);
    void clearOfflineFiles().then(() => setSyncStamp((n) => n + 1));
  }, []);

  return {
    enabled,
    progress,
    summary: offlineSummary(docs ?? []),
    lastChecked: lastCheckedAt(),
    enable,
    disable,
  };
}
